import L from 'leaflet';

/* Radovi, blokade i nezgode na mapi.

   ── VRSTE SU GRUBO GRUPISANE, NAMERNO ─────────────────────────────────────
   Provajder razlikuje četrnaest vrsta — kiša, magla, led, vetar, poplava,
   opasni uslovi… Dispečeru je to jedna te ista poruka („uslovi na putu"), a
   četrnaest ikonica na mapi niko ne razlikuje u letu. Ovde ih je sedam, i
   svaka menja odluku drugačije:

     ZATVOREN  — ne može se proći, ruta se menja
     RADOVI    — može, ali sporije i planirano (traje danima)
     NEZGODA   — može, ali neizvesno (može se raščistiti za pola sata)
     TRAKA     — sužen protok
     GUZVA     — zastoj bez uzroka koji se prijavljuje
     OBILAZAK  — postoji propisana zaobilaznica
     USLOVI    — vreme, kolovoz
     OSTALO    — sve što provajder nije prepoznao (ne odbacuje se) */

export const VRSTE = {
    ZATVOREN: { naziv: 'gps.incZatvoren', boja: '#c62828', ikona: 'pi pi-ban' },
    RADOVI:   { naziv: 'gps.incRadovi',   boja: '#ef6c00', ikona: 'pi pi-wrench' },
    NEZGODA:  { naziv: 'gps.incNezgoda',  boja: '#d81b60', ikona: 'pi pi-exclamation-triangle' },
    TRAKA:    { naziv: 'gps.incTraka',    boja: '#f9a825', ikona: 'pi pi-arrows-h' },
    GUZVA:    { naziv: 'gps.incGuzva',    boja: '#fb8c00', ikona: 'pi pi-clock' },
    OBILAZAK: { naziv: 'gps.incObilazak', boja: '#6a1b9a', ikona: 'pi pi-directions-alt' },
    USLOVI:   { naziv: 'gps.incUslovi',   boja: '#0277bd', ikona: 'pi pi-cloud' },
    OSTALO:   { naziv: 'gps.incOstalo',   boja: '#607d8b', ikona: 'pi pi-info-circle' },
};

export const vrstaOpis = (v) => VRSTE[v] || VRSTE.OSTALO;

/* Oznaka je KAP sa ikonom, kao pin pretrage a ne kao kružić stajanja —
   incident nije nešto što je vozilo uradilo nego nešto što ga čeka, pa ne sme
   da se pomeša sa oznakama sopstvene rute.

   ── ZAŠTO JE VELIKA ────────────────────────────────────────────────────────
   Ovo je jedini sloj koji se pali da bi se NEŠTO NAŠLO, a ne da bi se gledalo.
   Dispečer ga upali, pređe pogledom preko mape i mora da vidi gde je problem —
   bez traženja. Zato je 34px, sa belim obrubom i senkom koji je drže čitljivom
   i nad linijskom mapom i nad satelitom, gde pozadina može biti bilo koje boje.

   Zatvoren put dodatno dobija prsten koji pulsira: to je jedina vrsta posle
   koje ruta više ne važi, i mora da se nađe i kad je mapa puna. */
/* Ikone se prave JEDNOM po vrsti i pamte.

   Ovo je bio glavni razlog kočenja. Funkcija se zvala unutar `.map()` pri
   svakom iscrtavanju, pa je svaki od nekoliko stotina markera dobijao NOV
   `L.divIcon` objekat. React-Leaflet poredi ikonu po referenci: nova
   referenca znači `setIcon`, a `setIcon` ruši i ponovo pravi DOM markera.
   Pošto se mapa iscrtava na svako osvežavanje pozicija, to se ponavljalo
   svakih nekoliko sekundi nad celim slojem.

   Kombinacija (vrsta × zatvoren × pulsira) ima najviše 32 vrednosti, a
   Leaflet od jedne ikone pravi zaseban DOM za svaki marker — deljenje je
   bezbedno i predviđeno. */
const kesIkona = new Map();

export const ikonaIncidenta = (vrsta, zatvoren, pulsira) => {
    const kljuc = `${vrsta}|${zatvoren ? 1 : 0}|${pulsira ? 1 : 0}`;
    const gotova = kesIkona.get(kljuc);
    if (gotova) return gotova;

    const o = vrstaOpis(vrsta);
    const klase = ['gps-inc'];
    if (zatvoren) klase.push('gps-inc-zatvoren');
    if (zatvoren && pulsira) klase.push('gps-inc-puls');

    const ikona = L.divIcon({
        className: 'gps-inc-ikona',
        html: `<span class="${klase.join(' ')}" style="--inc-boja:${o.boja}">`
             + `<i class="${o.ikona}"></i></span>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
        popupAnchor: [0, -16],
    });

    kesIkona.set(kljuc, ikona);
    return ikona;
};

/* Iznad ovoliko zatvorenih puteva pulsiranje se GASI.

   Dva razloga, i oba vode istom broju. Prvi je smisao: prsten koji pulsira
   postoji da bi se zatvoren put našao u gustoj mapi — kad ih pulsira
   sedamdeset, ništa se ne ističe i oznaka je izgubila svrhu. Drugi je cena:
   svaka animacija je zaseban sloj koji se crta svakih 16 ms, a mapa se pri
   tome još i pomera. */
export const PULS_NAJVISE = 12;

/* 1500 s -> „25 min". Kašnjenje ispod minuta se ne prikazuje — nije podatak
   po kom se odlučuje, a red u kartici troši. */
export const kasnjenjeTeksta = (s) => {
    if (s == null || s < 60) return null;
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h ? `${h}h ${m}min` : `${m}min`;
};

/* ISO datum iz provajdera -> „30.08.2026. 14:20".

   Ovo je jedino mesto gde front formatira datum, i to svesno: podatak ne dolazi
   iz naše baze nego iz spoljnog servisa, pa ga SP nema gde oblikovati (pravilo
   iz CLAUDE.md se odnosi na naše podatke). */
export const vremeTeksta = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const p = (x) => String(x).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}. ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/* Sažetak za traku iznad mape: koliko ih je i koliko ukupno koštaju.

   Zatvoreni putevi se broje ODVOJENO i ne ulaze u zbir kašnjenja — zatvoren
   put nije „plus dvadeset minuta" nego „ovuda se ne prolazi", i sabrati ga sa
   zastojima značilo bi slagati dispečera. */
export const sazetakIncidenata = (lista) => {
    const l = lista || [];
    const zatvorenih = l.filter(x => x.zatvoren).length;
    const kasni = l.filter(x => !x.zatvoren && x.kasnjenje > 0)
                   .reduce((z, x) => z + x.kasnjenje, 0);
    return {
        ukupno: l.length,
        zatvorenih,
        ozbiljnih: l.filter(x => x.ozbiljno).length,
        kasnjenje: kasni || null,
    };
};

/* Ide u innerHTML oblačića, pa se propušta kroz escape: naslov i opis dolaze iz
   odgovora servisa i takav sadržaj se ne ugrađuje u markup neproveren. */
const ZNACI = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
const bezbedno = (v) => String(v == null ? '' : v).replace(/[&<>"]/g, c => ZNACI[c]);

/* Osnovni podaci na PRELAZAK MIŠEM.

   ── ZAŠTO SE VEZUJE TEK PRI PRELASKU ───────────────────────────────────────
   Primamljivo je staviti <Tooltip> kao dete svakog markera, ali React-Leaflet
   iscrtava decu oblačića odmah po montiranju, kao i kod kartice. Sa nekoliko
   stotina oznaka to je isti trošak koji je jednom već zaustavljao mapu (OP-81).

   Ovako oblačić ne postoji dok miš ne dođe na oznaku. Posle prvog vezivanja
   Leaflet ga sam otvara i zatvara — ovo se plaća jednom po oznaci, i to samo za
   one koje su stvarno pogledane.

   Sadržaj je NAMERNO kratak: vrsta, deonica, koliko košta. Sve ostalo (trajanje,
   opis, veza ka Google mapama) čeka klik — oblačić koji se prevuče preko pola
   mape prestaje da bude oblačić. */
export const veziOblacic = (marker, inc, t) => {
    if (!marker || marker.getTooltip()) return;

    const o = vrstaOpis(inc.vrsta);
    const kasni = kasnjenjeTeksta(inc.kasnjenje);
    const km = inc.kmOdPocetka != null
        ? `${inc.kmOdPocetka.toLocaleString('sr-RS', { maximumFractionDigits: 1 })} km`
        : '';

    /* Zatvoren put nema kašnjenje i to nije propust podatka — nije „sporije"
       nego „ne može", pa umesto minuta stoji sama ta reč. */
    const dole = [inc.zatvoren ? t('gps.incPutZatvoren') : (kasni ? `+${kasni}` : ''), km]
        .filter(Boolean).join('  ·  ');

    marker.bindTooltip(
        `<span class="gps-inch-vrsta" style="color:${o.boja}">${bezbedno(t(o.naziv))}</span>`
        + (inc.naslov ? `<span class="gps-inch-gde">${bezbedno(inc.naslov)}</span>` : '')
        + (dole ? `<span class="gps-inch-dole">${bezbedno(dole)}</span>` : ''),
        { direction: 'top', offset: [0, -16], opacity: 1, className: 'gps-inch' }
    );
    marker.openTooltip();
};

/* Deonica na koju se oznaka odnosi, u boji te oznake.

   Oznaka kaže da negde ovde ima radova; linija kaže KOJA je to ulica i dokle se
   proteže. Bez nje se kod raskrsnice ne zna ni koja je od tri ulice u pitanju.

   Crta se dvaput — beli obrub ispod, boja iznad. Isti postupak kao kod trasa
   plana: bez obruba se tanka linija nad gustom mapom prosto ne uhvati, a ovo je
   oznaka koja se pali da bi se NEŠTO NAŠLO.

   `interactive: false` je bitno: linija ne sme da hvata klik, inače otima klik
   samoj oznaci i karticu više ne možeš da otvoriš. */
export const TRAGOVI_DEONICE = (boja) => [
    { color: '#ffffff', weight: 12, opacity: 0.85, interactive: false },
    { color: boja, weight: 6, opacity: 0.95, interactive: false },
];
